// Saccount.jsx
import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import Avatar from "../../Images/Avatar.png";
import { BASEAPIURL, token } from "../../env";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const Saccount = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState("A");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountStatement, setAccountStatement] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showStatementModal, setShowStatementModal] = useState(false);

  const fetchMembers = async (page = pageNumber, letter = selectedLetter) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${BASEAPIURL}/MemberListwithfilter?pageSize=28&pageNumber=${page}&startsWith=${letter}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        }
      );
      if (!response.ok) throw new Error(`No Member Found`);
      const result = await response.json();
      console.log("Members API Response:", result);
      if (result.data) {
        const updatedMembers = result.data.map((member) => ({
          ...member,
          Occupations: Array.isArray(member.Occupations)
            ? member.Occupations
            : member.Occupations && typeof member.Occupations === "object"
            ? [member.Occupations]
            : [],
        }));
        setMembers(updatedMembers);
        setFilteredMembers(updatedMembers);
        setHasMore(updatedMembers.length === 28);
      } else {
        setMembers([]);
        setFilteredMembers([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Member fetch error:", err);
      setError(err.message || "Failed to fetch members");
      setMembers([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembersAll = async (
    page = pageNumber,
    letter = selectedLetter
  ) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        `${BASEAPIURL}/AllMemberListwithfilter?pageSize=28&pageNumber=${page}&startsWith=${letter}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tokenData: token }),
        }
      );
      if (!response.ok) throw new Error(`No Member Found`);
      const result = await response.json();
      console.log("Members API Response:", result);
      if (result.data) {
        const updatedMembers = result.data.map((member) => ({
          ...member,
          Occupations: Array.isArray(member.Occupations)
            ? member.Occupations
            : member.Occupations && typeof member.Occupations === "object"
            ? [member.Occupations]
            : [],
        }));
        setMembers(updatedMembers);
        setFilteredMembers(updatedMembers);
        setHasMore(updatedMembers.length === 28);
      } else {
        setMembers([]);
        setFilteredMembers([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Member fetch error:", err);
      setError(err.message || "Failed to fetch members");
      setMembers([]);
      setFilteredMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLetter) {
      fetchMembers(pageNumber, selectedLetter);
    }
  }, [selectedLetter]);

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        (async () => {
          fetchMembersAll(pageNumber, searchTerm);
        })();
      }, 400);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [loading, pageNumber]);

  const handleFilterSearch = (letter) => {
    if (letter) {
      setSearchTerm(letter);
      if (selectedLetter) {
        setSelectedLetter("");
        setPageNumber(1);
      }
    } else {
      setSearchTerm("");
      setSelectedLetter("A");
      setPageNumber(1);
    }
  };

  const handleFilterLetter = (letter) => {
    setSelectedLetter(letter);
    if (searchTerm) {
      setSearchTerm("");
      setPageNumber(1);
    }
  };

  const fetchAccountStatement = async (member) => {
    try {
      const headers = new Headers({ "Content-Type": "application/json" });
      const body = JSON.stringify({ tokenData: token });
      const response = await fetch(
        `${BASEAPIURL}/StatementOfAcListByMemID?MemID=${member.MemID}`,
        {
          method: "POST",
          headers,
          body,
        }
      );
      const result = await response.json();
      if (result?.data) {
        const data = Array.isArray(result.data) ? result.data : [result.data];
        const sorted = data.sort(
          (a, b) => new Date(b.VouDate) - new Date(a.VouDate)
        );
        setAccountStatement(sorted);
        setSelectedMember(member);
        setShowStatementModal(true);
      } else {
        alert("No statement data found.");
      }
    } catch (err) {
      console.error("Statement fetch failed:", err);
      alert("Failed to load statement.");
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(members);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Members");
    XLSX.writeFile(wb, "members_list.xlsx");
  };

  const exportStatementToExcel = () => {
    const worksheetData = accountStatement.map((row) => ({
      "Voucher No": row.VNo || "N/A",
      Date: new Date(row.VouDate).toLocaleDateString("en-GB"),
      Description: row.VDesc || "No description",
      Location: row.VType?.trim() || "N/A",
      "Bill Amount": row.VAmount,
      Debit: row.DebitAmount,
      Credit: row.CreditAmount,
    }));

    worksheetData.push({});
    worksheetData.push({
      Description: "Sub Total",
      Debit: totalDebit,
      Credit: totalCredit,
    });
    worksheetData.push({
      Description: "Balance",
      Debit: netBalance > 0 ? netBalance : 0,
      Credit: netBalance < 0 ? Math.abs(netBalance) : 0,
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Statement");
    XLSX.writeFile(
      workbook,
      `Statement_${selectedMember?.MemName || "member"}.xlsx`
    );
  };

  const exportStatementToPdf = () => {
    const doc = new jsPDF({
      orientation: "landscape",
    });

    var xOffset = doc.internal.pageSize.width / 2;
    doc.setFontSize(12);
    doc.text("Statement of Account", xOffset, 8, { align: "center" });
    doc.setFontSize(10);
    doc.text(
      `${selectedMember?.MemName || "member"} (${selectedMember?.MemMSNO})`,
      xOffset,
      12,
      { align: "center" }
    );

    const tableData = accountStatement?.map((item) => {
      return [
        `${item?.VNo}`,
        `${item?.VouDate}`,
        `${item?.VDesc}`,
        ``,
        `${item?.VAmount}`,
        `${item?.DebitAmount}`,
        `${item?.CreditAmount}`,
      ];
    });

    const tableHeaders = [
      "Voucher No",
      "Date",
      "Description",
      "Location",
      "Bill Amount",
      "Debit",
      "Credit",
    ];

    const tableFooterSubtotal = [
      "Sub Total",
      "",
      "",
      "",
      "",
      `${totalDebit} Dr`,
      `${totalCredit} Cr`,
    ];

    const tableFooterBalance = [
      "Balance",
      "",
      "",
      "",
      "",
      `${netBalance > 0 ? netBalance : "0"} Dr`,
      `${netBalance < 0 ? Math.abs(netBalance) : "0"} Cr`,
    ];

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      foot: [tableFooterSubtotal, tableFooterBalance],
      showHead: "firstPage",
      showFoot: "lastPage",
      theme: "striped",
      columnStyles: { text: { cellWidth: "auto" } },
      margin: { left: 3, right: 3 },
      styles: {
        overflow: "linebreak",
        cellWidth: "wrap",
        cellPadding: 1,
        fontSize: 6,
      },
      autoWidth: true,
    });

    doc.save(`Statement_${selectedMember?.MemName || "member"}.pdf`);
  };

  const formatCurrency = (num) => `₹${Number(num || 0).toFixed(2)}`;

  const { totalCredit, totalDebit, netBalance } = accountStatement.reduce(
    (totals, transaction) => {
      const credit = Number(transaction.CreditAmount || 0);
      const debit = Number(transaction.DebitAmount || 0);
      const net = credit - debit;
      return {
        totalCredit: totals.totalCredit + credit,
        totalDebit: totals.totalDebit + debit,
        netBalance: totals.netBalance + net,
      };
    },
    { totalCredit: 0, totalDebit: 0, netBalance: 0 }
  );

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const getImage = (member) => {
    if (member?.ImageBase64) {
      return member.ImageBase64.startsWith("data:")
        ? member.ImageBase64
        : `data:image/jpeg;base64,${member.ImageBase64}`;
    }
    return Avatar;
  };

  return (
    <div className="container py-2">
      <h2 className="mb-4 fw-semibold text-center text-dark">
        Statement Of Account
      </h2>

      {/* Search Bar */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control form-control-lg"
          placeholder="🔍 Search by member name..."
          value={searchTerm}
          onChange={(e) => handleFilterSearch(e.target.value)}
        />
      </div>

      {/* Alphabet Filter */}
      <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
        {alphabet.map((char) => (
          <button
            key={char}
            className={`btn btn-sm ${
              selectedLetter === char ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => handleFilterLetter(char)}
          >
            {char}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger text-center">{error}</div>}

      {/* Member Grid */}
      <div className="row g-4">
        {loading ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredMembers.length > 0 ? (
          filteredMembers.map((member) => (
            <div key={member.MemID} className="col-sm-6 col-md-4 col-lg-3">
              <div
                className="card p-3 text-center shadow-sm h-100"
                style={{ cursor: "pointer", minHeight: "250px" }}
                onClick={() => fetchAccountStatement(member)}
              >
                <img
                  src={getImage(member)}
                  alt={member.MemName}
                  className="rounded-circle mb-2"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "cover",
                    border: "2px solid #007bff",
                    margin: "auto",
                  }}
                />
                <h5 className="h6">{member.MemName}</h5>
                <p className="text-muted small mb-1">
                  MemberShip No: {member.MemMSNO}
                </p>
                <p className="text-muted small mb-1">
                  Outstanding: {member.Outstanding}
                </p>
                <button
                  className="btn btn-sm btn-outline-primary mt-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchAccountStatement(member);
                  }}
                >
                  View Statement
                </button>
              </div>
            </div>
          ))
        ) : (
          !loading && (
            <p className="text-center text-muted col-12">
              No members found matching "{searchTerm}".
            </p>
          )
        )}
      </div>

      {/* Pagination */}
      {!searchTerm && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={pageNumber === 1 || loading}
          >
            ← Previous
          </button>
          <span className="fw-bold">Page {pageNumber}</span>
          <button
            className="btn btn-outline-primary"
            onClick={() => setPageNumber((prev) => prev + 1)}
            disabled={!hasMore || loading}
          >
            Next →
          </button>
        </div>
      )}

      {/* Statement Modal */}
      {showStatementModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Account Statement - {selectedMember?.MemName} (
                  {selectedMember?.MemMSNO})
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStatementModal(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body p-0">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle m-0">
                    <thead className="table-success text-white text-center">
                      <tr>
                        <th>Voucher No</th>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Location</th>
                        <th>Bill Amount</th>
                        <th className="text-danger">Debit</th>
                        <th className="text-success">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountStatement.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted">
                            No statement found
                          </td>
                        </tr>
                      ) : (
                        accountStatement.map((tx, i) => (
                          <tr key={i}>
                            <td className="text-center">{tx.VNo}</td>
                            <td className="text-center">
                              {new Date(tx.VouDate).toLocaleDateString()}
                            </td>
                            <td className="text-center">{tx.VDesc}</td>
                            <td className="text-center">{tx.VType}</td>
                            <td className="text-center fw-bold">
                              {formatCurrency(tx.VAmount)}
                            </td>
                            <td className="text-center text-danger">
                              {formatCurrency(tx.DebitAmount)}
                            </td>
                            <td className="text-center text-success">
                              {formatCurrency(tx.CreditAmount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5" className="text-center fw-bold">
                          Sub Total
                        </td>
                        <td className="text-center text-danger">
                          {formatCurrency(totalDebit)} Dr
                        </td>
                        <td className="text-center text-success">
                          {formatCurrency(totalCredit)} Cr
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="5" className="text-center fw-bold">
                          Balance
                        </td>
                        <td
                          className={`text-center ${
                            netBalance > 0 ? "text-danger" : ""
                          }`}
                        >
                          {netBalance > 0 ? formatCurrency(netBalance) : "0"} Dr
                        </td>
                        <td
                          className={`text-center ${
                            netBalance < 0 ? "text-success" : ""
                          }`}
                        >
                          {netBalance < 0
                            ? formatCurrency(Math.abs(netBalance))
                            : "0"}{" "}
                          Cr
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowStatementModal(false)}
                >
                  Close
                </button>
                <button
                  className="btn btn-success"
                  onClick={exportStatementToPdf}
                >
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Saccount;
